import { z } from 'zod';
import { PERSON_KEYS, EVENT_TEMPLATE_KINDS } from '../types/event';

const timezoneSchema = z.string().min(1).max(100);

// Tiptap doc JSON. We don't validate the node graph here — Tiptap's
// schema-validate happens client-side. We do bound the byte size to prevent
// payload bombs (a 64 KB doc is already a huge document).
const bodyJsonSchema = z
  .unknown()
  .nullable()
  .refine(
    (v) => v === null || JSON.stringify(v).length <= 64 * 1024,
    { message: 'Body must be under 64 KB' },
  );

export const createEventSchema = z
  .object({
    projectId: z.string().uuid(),
    name: z.string().min(1, 'Event name is required').max(255),
    description: z.string().max(2000).nullable().optional(),
    startAt: z.string().datetime({ offset: true }),
    endAt: z.string().datetime({ offset: true }),
    timezone: timezoneSchema,
    location: z.string().max(255).nullable().optional(),
    sortOrder: z.number().int().min(0).optional().default(0),
  })
  .refine((d) => new Date(d.endAt) > new Date(d.startAt), {
    message: 'End must be after start',
    path: ['endAt'],
  });

export const updateEventSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(2000).nullable().optional(),
    startAt: z.string().datetime({ offset: true }).optional(),
    endAt: z.string().datetime({ offset: true }).optional(),
    timezone: timezoneSchema.optional(),
    location: z.string().max(255).nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine(
    (d) => !d.startAt || !d.endAt || new Date(d.endAt) > new Date(d.startAt),
    { message: 'End must be after start', path: ['endAt'] },
  );

export const createEventStepSchema = z
  .object({
    eventId: z.string().uuid(),
    title: z.string().min(1, 'Step title is required').max(255),
    category: z.string().max(100).nullable().optional(),
    startAt: z.string().datetime({ offset: true }),
    endAt: z.string().datetime({ offset: true }).nullable().optional(),
    bodyJson: bodyJsonSchema.optional(),
    bodyText: z.string().max(64 * 1024).nullable().optional(),
    includeInSummary: z.boolean().optional().default(true),
    sortOrder: z.number().int().min(0).optional().default(0),
  })
  .refine(
    (d) => !d.endAt || new Date(d.endAt) > new Date(d.startAt),
    { message: 'End must be after start', path: ['endAt'] },
  );

export const updateEventStepSchema = z
  .object({
    title: z.string().min(1).max(255).optional(),
    category: z.string().max(100).nullable().optional(),
    startAt: z.string().datetime({ offset: true }).optional(),
    endAt: z.string().datetime({ offset: true }).nullable().optional(),
    bodyJson: bodyJsonSchema.optional(),
    bodyText: z.string().max(64 * 1024).nullable().optional(),
    includeInSummary: z.boolean().optional(),
    sortOrder: z.number().int().min(0).optional(),
  });

export const createStepParticipantSchema = z
  .object({
    projectVendorId: z.string().uuid().nullable().optional(),
    personKey: z.enum(PERSON_KEYS).nullable().optional(),
    freeText: z.string().min(1).max(255).nullable().optional(),
    role: z.string().max(100).nullable().optional(),
  })
  .refine(
    (d) => {
      const set = [d.projectVendorId, d.personKey, d.freeText].filter(
        (v) => v !== null && v !== undefined && v !== '',
      ).length;
      return set === 1;
    },
    {
      message: 'Exactly one of projectVendorId, personKey, or freeText must be set',
      path: ['projectVendorId'],
    },
  );

// ─── Templates ───────────────────────────────────────────────────────────────

export const createEventTemplateSchema = z.object({
  fromEventId: z.string().uuid(),
  name: z.string().min(1, 'Template name is required').max(255),
  description: z.string().max(2000).nullable().optional(),
  kind: z.enum(EVENT_TEMPLATE_KINDS).optional().default('custom'),
});

const participantMappingValueSchema = z
  .object({
    projectVendorId: z.string().uuid().nullable().optional(),
    personKey: z.enum(PERSON_KEYS).nullable().optional(),
  })
  .refine(
    (d) => {
      const set = [d.projectVendorId, d.personKey].filter(
        (v) => v !== null && v !== undefined,
      ).length;
      return set <= 1;
    },
    { message: 'Provide at most one of projectVendorId or personKey' },
  );

export const createEventFromTemplateSchema = z.object({
  projectId: z.string().uuid(),
  templateId: z.string().uuid(),
  name: z.string().min(1).max(255),
  description: z.string().max(2000).nullable().optional(),
  startAt: z.string().datetime({ offset: true }),
  timezone: timezoneSchema,
  location: z.string().max(255).nullable().optional(),
  participantMapping: z.record(z.string().max(100), participantMappingValueSchema).optional().default({}),
});

export type CreateEventInput = z.infer<typeof createEventSchema>;
export type UpdateEventInput = z.infer<typeof updateEventSchema>;
export type CreateEventStepInput = z.infer<typeof createEventStepSchema>;
export type UpdateEventStepInput = z.infer<typeof updateEventStepSchema>;
export type CreateStepParticipantInput = z.infer<typeof createStepParticipantSchema>;
export type CreateEventTemplateInput = z.infer<typeof createEventTemplateSchema>;
export type CreateEventFromTemplateInput = z.infer<typeof createEventFromTemplateSchema>;
